import { HttpErrorResponse } from '@angular/common/http'
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, effect, inject, Signal, signal } from '@angular/core'
import { RouterModule } from '@angular/router'
import { BlockUiComponent } from '@isl-core/components/block-ui/block-ui.component'
import { HeaderComponent } from '@isl-core/components/header/header.component'
import { UserInterface } from '@isl-core/interfaces/user.interface'
import { AlertService } from '@isl-core/services/alert.service'
import { CityService } from '@isl-core/services/city.service'
import { DataService } from '@isl-core/services/data.service'
import { ErrorService } from '@isl-core/services/error.service'
import { MenuService } from '@isl-core/services/menu.service'
import { RouterStateService } from '@isl-core/services/router-state.service'
import { UserService } from '@isl-core/services/user.service'
import { KEYCLOAK_EVENT_SIGNAL, KeycloakEventType, ReadyArgs, typeEventArgs } from 'keycloak-angular'
import Keycloak from 'keycloak-js'
import { MenuItem, MessageService } from 'primeng/api'
import { ButtonModule } from 'primeng/button'
import { Toast } from 'primeng/toast'

@Component({
    selector: 'app-root',
    imports: [ButtonModule, RouterModule, Toast, HeaderComponent, ButtonModule, BlockUiComponent],
    templateUrl: './app.component.html',
    standalone: true,
    providers: [MessageService],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AppComponent {
    authenticated = signal(false)
    city = signal<string>('')
    isDataLoaded = signal(false)
    keycloakStatus = signal<string | undefined>(undefined)
    menu = signal<Signal<MenuItem[]>>(signal([]))
    title = signal('ISL Labour Tracking and Reporting')
    user = signal<Signal<UserInterface | null>>(signal(null))
    userMenu = signal<Signal<MenuItem[]>>(signal([]))
    userRoles = signal<Signal<string[]>>(signal([]))
    isUserLoaded = signal(false)
    areRolesLoaded = signal(false)

    private alertService = inject(AlertService)
    private cdr = inject(ChangeDetectorRef)
    private cityService = inject(CityService)
    private errorService = inject(ErrorService)
    private menuService = inject(MenuService)
    private messageService = inject(MessageService)
    private userService = inject(UserService)
    private dataService = inject(DataService)
    private routerStateService = inject(RouterStateService)

    private readonly keycloakService = inject(Keycloak)
    private readonly keycloakSignal = inject(KEYCLOAK_EVENT_SIGNAL)

    constructor() {
        this.city.set(this.cityService.get()())
        this.menu.set(this.menuService.get())
        this.user.set(this.userService.get())
        this.userMenu.set(this.menuService.getUserMenu())
        this.userRoles.set(this.userService.roles)

        this.initErrorHandler().initAlertHandler().initRouter().initData()

        const keycloak = inject(Keycloak)
        this.checkKeycloakEvents(keycloak)
    }

    async initData(): Promise<this> {
        effect(async () => {
            const city = this.cityService.get()()

            this.city.set(city)

            setTimeout(async () => {
                await this.dataService.initData()
                this.isDataLoaded.set(true)
            }, 0)
        })

        return this
    }

    initErrorHandler(): this {
        effect(() => {
            const error = this.errorService.get()()
            if (error === null) {
                return
            }

            const httpError = error as HttpErrorResponse
            if (
                httpError !== undefined &&
                httpError.status !== undefined &&
                (httpError.status === 401 || httpError.status === 403)
            ) {
                this.logout()
            }

            this.messageService.add({
                severity: 'error',
                summary: error?.name,
                detail: error?.message,
                life: 5000
            })

            this.cdr.detectChanges()
        })

        return this
    }

    initAlertHandler(): this {
        effect(() => {
            const alert = this.alertService.get()()
            if (alert === null) {
                return
            }

            this.messageService.add(alert)
        })

        return this
    }

    initRouter(): this {
        this.routerStateService.subscribeRouterStateChanges()

        return this
    }

    checkKeycloakEvents(keycloak: Keycloak) {
        const keycloakEvent = this.keycloakSignal()

        effect(() => {
            this.keycloakStatus.set(keycloakEvent.type)

            if (keycloakEvent.type === KeycloakEventType.Ready) {
                this.authenticated.set(typeEventArgs<ReadyArgs>(keycloakEvent.args))
            }

            if (keycloakEvent.type === KeycloakEventType.AuthLogout) {
                this.authenticated.set(false)
            }

            if (keycloak && keycloak.realmAccess && keycloak.realmAccess.roles) {
                if (!this.areRolesLoaded()) {
                    this.userService.setRoles(keycloak.realmAccess.roles)
                    this.areRolesLoaded.set(true)
                }
            }

            if (!this.isUserLoaded()) {
                const tokenParsed = keycloak.tokenParsed
                this.isUserLoaded.set(true)
                if (tokenParsed) {
                    this.userService.set({
                        id: tokenParsed.sub as string,
                        username: tokenParsed['preferred_username'] as string,
                        firstName: '' as string,
                        lastName: '' as string,
                        isLogged: true
                    })
                }
            }
        })
    }

    changeCity(city: string) {
        this.cityService.set(city)
    }

    logout() {
        this.isUserLoaded.set(false)
        this.keycloakService.logout()
    }
}
